// src/RiskMitigationPlan.js
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Drawer, Box, Typography, IconButton, Divider, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Tooltip, Badge, Snackbar, Alert,
  TableContainer, Paper
} from '@mui/material';
import {
  Close as CloseIcon, SmartToyOutlined as BotIcon,
  Save as SaveIcon, DeleteOutline as DeleteIcon,
  Edit as EditIcon, Check as CheckIcon,
  CheckCircle as CheckCircleIcon, TuneSharp as TuneIcon,
  ListAltOutlined as DetailsIcon
} from '@mui/icons-material';

import {
  fetchControls, addSavedControl, discardControl, discardSavedControl,
  editGeneratedControl, editSavedControl
} from './redux/actions/controlActions';

import ControlEvaluationForm from './ControlEvaluation';
//import useSaveOperation from './useSaveOperation';

import {
  DndContext, PointerSensor, useSensor, useSensors,
  DragOverlay, closestCenter
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

/*------------- tiny helpers -------------*/
const head = { backgroundColor:'#F9FAFB', color:'#6B7280', fontWeight:500 };

const riskPriorityColors = {
  Critical:    '#1A1A1A',
  High:        '#993333',
  Significant: '#B94D4D',
  Moderate:    '#D4AA7D',
  Low:         '#B8C2B7',
  Negligible:  '#D8D8D8',
  None:        '#EBEBEB',
};

const FloatingSaveBtn = ({ view, toggle, gen, saved }) => (
  <Tooltip title={view==='saved'?'Show generated':'Show saved'} arrow>
    <Badge badgeContent={view==='saved'?gen:saved} color="primary">
      <IconButton
        onClick={toggle}
        sx={{ backgroundColor: '#FFFFFF', boxShadow: 3, borderRadius: '50%' }}
      >
        {view==='saved'? <BotIcon/> : <SaveIcon/>}
      </IconButton>
    </Badge>
  </Tooltip>
);

const FloatingTrashBtn = ({ trash, onClick }) => (
  <Tooltip title="Discarded actions" arrow>
    <Badge badgeContent={trash} color="error">
      <IconButton
        onClick={onClick}
        sx={{ backgroundColor: '#FFFFFF', boxShadow: 3, borderRadius: '50%' }}
      >
        <DeleteIcon/>
      </IconButton>
    </Badge>
  </Tooltip>
);

/*------------- Draggable row -------------*/

function DraggableControlRow({
  control, isSaved, onConfirmEdit, onDiscard, onSave,
  evaluationData, riskId, modelId, assessmentUuid
}) {
  const [edit, setEdit]   = useState(false);
  const [txt,  setTxt]    = useState(control.name);
  const [evalOpen,setEvalOpen]=useState(false);

  /* dnd hooks */
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSensor ? {} : {}; // fallback – if DnD kit removed elsewhere

  const style = transform ? { transform: CSS.Translate.toString(transform), transition } : {};

  const done = evaluationData.control_recurrence || evaluationData.executed_next || evaluationData.self_evaluation;

  const handleSaveClick = () => onSave(control);

  return (
    <>
      <TableRow ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <TableCell>
          {done && <CheckCircleIcon color="success" fontSize="small"/>}
        </TableCell>
        <TableCell sx={{ maxWidth:400 }}>
          {edit
            ? <input value={txt} onChange={e=>setTxt(e.target.value)} style={{width:'100%'}}/>
            : control.name}
        </TableCell>
        <TableCell>
          <Chip size="small" label={control.controlModelStage||'–'}/>
        </TableCell>
        <TableCell>
          {edit ? (
            <>
              <IconButton size="small" onClick={()=>{
                onConfirmEdit(control, txt.trim(), isSaved);
                setEdit(false);
              }}><CheckIcon/></IconButton>
              <IconButton size="small" onClick={()=>setEdit(false)}>
                <CloseIcon fontSize="small"/>
              </IconButton>
            </>
          ) : (
            <>
              <IconButton size="small" onClick={()=>setEdit(true)}><EditIcon/></IconButton>
              {isSaved && (
                <Tooltip title={done?'Re-evaluate':'Details'} arrow>
                  <IconButton size="small" onClick={()=>setEvalOpen(true)}>
                    {done? <TuneIcon/> : <DetailsIcon/>}
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={()=>onDiscard(control)}>
                <DeleteIcon sx={{ color:'red' }}/>
              </IconButton>
              {!isSaved && (
                <IconButton size="small" onClick={handleSaveClick}>
                  <SaveIcon/>
                </IconButton>
              )}
            </>
          )}
        </TableCell>
      </TableRow>

      {/* evaluation drawer nested */}
      <Drawer anchor="right" open={evalOpen} onClose={()=>setEvalOpen(false)}>
        <Box sx={{ p:2, width:{xs:'100%', sm:'80%', md:'60%'} }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Action Details</Typography>
            <IconButton onClick={()=>setEvalOpen(false)}><CloseIcon/></IconButton>
          </Box>
          <Divider sx={{ my:2 }}/>
          <ControlEvaluationForm
            riskId={riskId}
            controlId={control.controlId}
            modelId={modelId}
            assessmentUuid={assessmentUuid}
          />
        </Box>
      </Drawer>
    </>
  );
}

/*------------- main deep-dive component -------------*/

function RiskMitigationPlan({ open, onClose, risk, modelId, assessmentUuid }) {
  const dispatch = useDispatch();
  //const { isSaving, isSaved } = useSaveOperation();

  /* selectors */
  const controlsObj     = useSelector(s=>s.controls.controls||{});
  const savedObj        = useSelector(s=>s.controls.savedControls||{});
  const discardedObj    = useSelector(s=>s.controls.discardedControls||{});
  const evalsObj        = useSelector(s=>s.controls.savedControlEvaluations||{});
  const loading         = useSelector(s=>s.controls.loading);
  const stateRisks      = useSelector(s=>s.risks.models[modelId]?.riskFormData || {});
  const sectionData     = stateRisks[risk.sectionName];

  /* filter */
  const generated       = controlsObj[risk.riskId]   || [];
  const saved           = savedObj[risk.riskId]   || [];
  const discarded       = discardedObj[risk.riskId]  || [];

  const generatedCount  = generated.length;
  const savedCount      = saved.length;
  const trashCount      = discarded.length;

  const [view,setView]  = useState(savedCount? 'saved':'generated');
  const [snack,setSnack]= useState(null);

  /* handlers */
  const confirmEdit = (ctrl,newName,isSavedRow) => {
    if (isSavedRow) {
      dispatch(editSavedControl   (risk.riskId, ctrl.controlId, newName));
    } else {
      dispatch(editGeneratedControl(risk.riskId, ctrl.controlId, newName));
    }
  };

  const handleGenerate = () => {
    dispatch(fetchControls({
      sectionName    : risk.sectionName,
      narratives     : null,
      riskSourceData : sectionData,
      sectionRisks   : [risk],
      controlModelType : null,
      controlModelStage: null,
    }));
  };

  const handleSave = (ctrl) => {
    dispatch(addSavedControl({ ...ctrl, status:'saved', modelId }));
  };

  const handleDiscard = (ctrl) => {
    if (view==='saved') {
      dispatch(discardSavedControl(ctrl, risk.riskId));
    } else {
      dispatch(discardControl(ctrl, risk.riskId));
    }
  };

  /* DnD overlay state */
  const [dragCtrl,setDragCtrl]=useState(null);
  const sensors=useSensors(useSensor(PointerSensor,{activationConstraint:{distance:5}}));

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ p:3, width:{ xs:'100%', sm:'80%', md:'60%' }, maxWidth:900 }}>
        {/* header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ maxWidth:'70%' }}>{risk.riskName}</Typography>
          <IconButton onClick={onClose}><CloseIcon/></IconButton>
        </Box>
        <Divider sx={{ mb:2 }}/>

        {/* Risk details table */}
        <TableContainer component={Paper} sx={{ mb:2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 500, width: 100 }}>Risk</TableCell>
                <TableCell>{risk.riskName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Source</TableCell>
                <TableCell>{risk.category || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Priority</TableCell>
                <TableCell>
                  <Chip
                    label={risk.priority || 'None'}
                    size="small"
                    sx={{
                      backgroundColor: riskPriorityColors[risk.priority] || riskPriorityColors.None,
                      color: '#fff'
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {/* floating buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
          <FloatingSaveBtn
            view={view}
            toggle={()=>setView(v=>v==='saved'?'generated':'saved')}
            gen={generatedCount}
            saved={savedCount}
          />
          <FloatingTrashBtn trash={trashCount} onClick={()=>setSnack({txt:'See Trash modal TODO',sev:'info'})}/>
        </Box>

        {view==='generated' && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<BotIcon/>}
            onClick={handleGenerate}
            disabled={loading}
            sx={{ mb:2 }}
          >
            {generatedCount? 'Refresh' : 'Generate'} Actions
          </Button>
        )}

        {/* table */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          //modifiers={[restrictToWindowEdges]}
          onDragStart={({active})=>{
            const all=[...generated,...saved];
            setDragCtrl(all.find(c=>c.name===active.id)||null);
          }}
          onDragEnd={()=>setDragCtrl(null)}
        >
          <TableContainer component={Paper} sx={{ mb:2 }}>
            <Table size="small" sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={head}>Evaluated?</TableCell>
                  <TableCell sx={head}>Action</TableCell>
                  <TableCell sx={head}>Stage</TableCell>
                  <TableCell sx={head}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(view==='saved'? saved:generated).map(ctrl=>{
                  const evaluation = evalsObj[risk.riskId]?.[ctrl.controlId]?.[0] || {};
                  return (
                    <DraggableControlRow
                      key={ctrl.controlId}
                      control={ctrl}
                      isSaved={view==='saved'}
                      onConfirmEdit={confirmEdit}
                      onDiscard={handleDiscard}
                      onSave={handleSave}
                      evaluationData={evaluation}
                      riskId={risk.riskId}
                      modelId={modelId}
                      assessmentUuid={assessmentUuid}
                    />
                  );
                })}
                {(view==='saved'? saved:generated).length===0 && (
                  <TableRow><TableCell colSpan={4} align="center">
                    {view==='saved'?'No saved actions':'No generated actions'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <DragOverlay>
            {dragCtrl && (
              <Box sx={{ px:2, py:1, bgcolor:'#fff', boxShadow:4, borderRadius:1 }}>
                {dragCtrl.name}
              </Box>
            )}
          </DragOverlay>
        </DndContext>

        {/* snackbar */}
        <Snackbar open={!!snack} autoHideDuration={4000} onClose={()=>setSnack(null)}>
          {snack && <Alert severity={snack.sev}>{snack.txt}</Alert>}
        </Snackbar>
      </Box>
    </Drawer>
  );
}

export default RiskMitigationPlan;